import React, { useState, useCallback } from 'react';
import { PlusOutlined, MenuFoldOutlined, ReloadOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Box,
  Stack,
  Inline,
  Text,
  ToggleInput,
  IconButton,
  FieldMessage,
  Draft,
  useThemeConfig,
} from '@resi-media/resi-ui';
import { useSetAtom } from 'jotai';
import { useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { useDrawer } from '@preview/contexts/DrawerContext';
import { useMockData } from '@preview/contexts/MockDataContext';
import { useRefresh } from '@preview/contexts/RefreshContext';
import { createChannelMock, createPlaylistMock, createSiteMock, createVideoMock } from '@preview/helpers/apiHelpers';
import { determineMockType } from '@preview/helpers/determineMockType';
import { getDataTypeGuard } from '@preview/helpers/getDataTypeGuard';
import { logoutAtom } from '@preview/store/auth';
import type { MockItem, FormValues } from '@preview/types';
import { AddMockModal } from '../AddMockModal';
import { BoxCastDataFetcherCard } from '../BoxCastDataFetcherCard';
import { EditMockModal } from '../EditMockModal';
import { EnhancedMockCard } from '../EnhancedMockCard';
import { SubSplashDataFetcherCard } from '../SubSplashDataFetcherCard';
import { ThreeJSScene } from '../ThreeJSScene';
import { VimeoDataFetcherCard } from '../VimeoDataFetcherCard';
import { YouTubeDataFetcherCard } from '../YouTubeDataFetcherCard';

export const MockControlPanel: React.FC = () => {
  const { setMode } = useThemeConfig();
  const logout = useSetAtom(logoutAtom);
  const {
    activeMocks,
    addApiMock,
    addChannelMock,
    addPlaylistMock,
    addSiteMock,
    addVideoMock,
    channelId,
    deleteMock,
    embedId,
    exportStore,
    importStore,
    mockConfig,
    mocks,
    pendingChanges,
    resetPendingChanges,
    resetToDefaults,
    toggleMock,
    updateMock,
    updateMockConfig,
  } = useMockData();

  const mockTemplates = React.useMemo(
    () => ({
      channel: createChannelMock({
        endpoint: '*/api/v1/channels?url=*',
        description: 'New Channel Mock',
        data: {
          channelId,
          channelName: 'New Channel',
          live: false,
          nextLive: null,
        },
      }),
      feed: createSiteMock({
        endpoint: '*/api/v1/sites?url=*',
        description: 'New Feed Mock',
        data: {
          id: 'site-123',
          customerId: 'test-customer',
          url: 'https://demo.resi.io',
          name: 'RESI Demo Site',
          description: 'A demo site for RESI on-demand content',
          channelId: '',
          branding: {
            mode: 'light',
          },
          feeds: [
            {
              id: 'feed-1',
              name: 'Main Feed',
              description: 'Primary content feed',
              default: true,
              playlists: [],
            },
          ],
        },
      }),
      playlist: createPlaylistMock({
        endpoint: '*/api/playlists/*',
        description: 'New Playlist Mock',
        data: {
          id: 'playlist-123',
          name: 'New Playlist',
          videos: [],
        },
      }),
      video: createVideoMock({
        endpoint: '/api/videos/*',
        description: 'New Video Mock',
        data: {
          id: 'video-123',
          title: 'New Video',
          description: 'A new video',
          thumbnail: { url: 'https://placehold.co/300x200?text=New+Video' },
          airDate: new Date().toISOString(),
          embedId,
          formattedDescription: '**A new video**',
          tags: [],
          playlists: [],
        },
      }),
    }),
    [channelId, embedId]
  );

  const { triggerRefresh } = useRefresh();
  const { setDrawerOpen } = useDrawer();

  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [selectedMock, setSelectedMock] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'3d' | 'cards'>('cards');

  // Modal context
  const { onCloseReset, setModal, setModalState } = Draft.ModalContext.useModal<{
    formData: FormValues;
    mock?: MockItem;
    mode: 'add' | 'edit';
  }>();

  // Form management
  const { reset: resetAddForm } = useForm<FormValues>({
    defaultValues: {
      endpoint: '',
      description: '',
      data: '',
    },
  });

  const { reset: resetEditForm } = useForm<FormValues>({
    defaultValues: {
      endpoint: '',
      description: '',
      data: '',
    },
  });

  const handleToggleMock = useCallback(
    (id: string) => {
      toggleMock(id);
      triggerRefresh();
      resetPendingChanges();
    },
    [resetPendingChanges, toggleMock, triggerRefresh]
  );

  const handleFormSubmit = (data: FormValues, isCreate = true) => {
    const baseData = {
      id: data.mockId,
      endpoint: data.endpoint,
      description: data.description,
      data: JSON.parse(data.data),
    };
    try {
      match(data.mockType)
        .with('channel', () => {
          addChannelMock(baseData, isCreate);
        })
        .with('feed', () => {
          addSiteMock(baseData, isCreate);
        })
        .with('playlist', () => {
          addPlaylistMock(baseData, isCreate);
        })
        .with('video', () => {
          addVideoMock(baseData, isCreate);
        })
        .otherwise(() => {
          addApiMock(baseData, isCreate);
        });

      isCreate && resetAddForm();
      onCloseReset();
      triggerRefresh();
      resetPendingChanges();
    } catch (error) {
      alert('Invalid JSON format');
    }
  };

  const showEditModal = (mock: MockItem) => {
    const mockType = determineMockType(mock);
    setSelectedMock(mock.id);
    setModalState({
      mode: 'edit',
      mock,
      formData: {
        mockId: mock.id,
        endpoint: mock.endpoint,
        description: mock.description || '',
        data: JSON.stringify(mock.data, null, 2),
        mockType,
      },
    });

    setModal(
      () => <EditMockModal mock={mock} onClose={onCloseReset} onSubmit={(data) => handleFormSubmit(data, false)} />,
      {
        title: 'Edit Mock',
        onCancel: onCloseReset,
      }
    );

    // Initialize form with mock data
    resetEditForm({
      endpoint: mock.endpoint,
      description: mock.description || '',
      data: JSON.stringify(mock.data, null, 2),
      mockType,
    });
  };

  const showAddModal = () => {
    setModalState({
      mode: 'add',
      formData: {
        mockId: '',
        endpoint: '',
        description: '',
        data: '{}',
        mockType: 'other',
      },
    });

    setModal(() => <AddMockModal mockTemplates={mockTemplates} onClose={onCloseReset} onSubmit={handleFormSubmit} />, {
      title: 'Add Mock',
      onCancel: onCloseReset,
    });
  };

  const handleDeleteMock = (id: string) => {
    // Confirm before deleting
    if (window.confirm('Are you sure you want to delete this mock?')) {
      deleteMock(id);
      triggerRefresh();
      resetPendingChanges();
    }
  };

  const handleResetToDefaults = () => {
    // Confirm before resetting
    if (window.confirm('Are you sure you want to reset all mocks to default values?')) {
      resetToDefaults();
      triggerRefresh();
      resetPendingChanges();
    }
  };

  // Export the entire store as a JSON file
  const handleExportStore = () => {
    try {
      const jsonData = exportStore();
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `resi-site-preview-mocks-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting store:', error);
      alert('Failed to export store data');
    }
  };

  // Import store data from a JSON file
  const handleImportStore = () => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';

    fileInput.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const jsonData = e.target?.result as string;
            const success = importStore(jsonData);

            if (success) {
              alert('Successfully imported store data');
              triggerRefresh();
            } else {
              alert('Failed to import store data. Invalid format.');
            }
          } catch (error) {
            console.error('Error importing store:', error);
            alert('Failed to import store data');
          }
        };

        reader.readAsText(file);
      }
    };

    // Trigger file selection dialog
    fileInput.click();
  };

  // Find relationships between mocks to highlight connected cards
  const findRelationships = React.useCallback(() => {
    const relationships: Record<string, string[] | undefined> = {};

    // Initialize empty arrays for each mock
    Object.keys(mocks).forEach((id) => {
      relationships[id] = [];
    });

    // Find feed -> playlist relationships
    Object.entries(mocks).forEach(([id, mock]) => {
      const { data } = mock;
      const { siteData } = getDataTypeGuard(data);
      if ((mock.endpoint.includes('sites') || mock.endpoint.includes('feed')) && siteData) {
        siteData.feeds.forEach((feed) => {
          if (feed.playlists.length > 0) {
            feed.playlists.forEach((playlist) => {
              // Find playlist mocks that match this ID
              Object.entries(mocks).forEach(([playlistId, playlistMock]) => {
                if (
                  playlistMock.endpoint.includes('playlists') &&
                  (playlistMock.endpoint.includes(`/${playlist.id}`) || playlistMock.endpoint.includes(`/${playlist}`))
                ) {
                  relationships[id]?.push(playlistId);
                }
              });
            });
          }
        });
      }
    });

    // Find playlist -> video relationships
    Object.entries(mocks).forEach(([id, mock]) => {
      const { data } = mock;
      const { playlistData } = getDataTypeGuard(data);
      if (mock.endpoint.includes('playlists') && playlistData) {
        playlistData.videos.forEach((video) => {
          // Find video mocks that match this ID
          Object.entries(mocks).forEach(([videoId, videoMock]) => {
            if (videoMock.endpoint.includes('videos') && videoMock.endpoint.includes(`/${video.id}`)) {
              relationships[id]?.push(videoId);
            }
          });
        });
      }
    });

    return relationships;
  }, [mocks]);

  // Determine if a mock is connected to any other mock
  const isConnected = useCallback(
    (mockId: string) => {
      const relationships = findRelationships();

      // Check if this mock has any outgoing connections
      if (relationships[mockId]?.length) return true;

      // Check if this mock has any incoming connections
      for (const [_sourceId, targetIds] of Object.entries(relationships)) {
        if (targetIds?.includes(mockId)) return true;
      }

      return false;
    },
    [findRelationships]
  );

  return (
    <Box
      css={(theme) => css`
        width: 400px;
        min-width: 400px;
        background-color: ${theme.palette.surface.default};
        border-right: 1px solid ${theme.palette.border};
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
        height: 100vh;
      `}
    >
      <Stack p="s">
        <Inline alignItems="center" gap="s" justifyContent="space-between">
          <Text weightVariant="semiBold">Mock Control Panel</Text>
          <IconButton label="Collapse" onClick={() => setDrawerOpen(false)} variant="ghost">
            <MenuFoldOutlined />
          </IconButton>
        </Inline>
        <Draft.Button
          label="Save & Refresh"
          onClick={() => {
            triggerRefresh();
            resetPendingChanges();
          }}
          startNode={<ReloadOutlined />}
          variant="contained"
          {...(pendingChanges > 0 && {
            endNode: (
              <Box
                as="span"
                css={(theme) => css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 20px;
                  height: 20px;
                  font-size: 12px;
                  line-height: 20px;
                  border-radius: 10px;
                  background-color: ${theme.palette.negative.main};
                  color: white;
                  font-weight: ${theme.typography.fontWeight.semiBold};
                `}
              >
                {pendingChanges}
              </Box>
            ),
          })}
        >
          Save & Refresh
        </Draft.Button>
      </Stack>

      <Stack
        css={css`
          overscroll-behavior: contain;
        `}
        gap="xl"
        overflowY="auto"
        p="s"
      >
        {/* Settings Panel - Always visible at the top */}
        <Draft.Card width="scale">
          <Draft.CardSection
            css={css`
              cursor: pointer;
            `}
            onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
          >
            <Inline alignItems="center" justifyContent="space-between">
              <Text>Site Override</Text>
              <ToggleInput
                checked={mockConfig.enabled}
                onChange={(e) => {
                  updateMockConfig({ enabled: e.target.checked });
                }}
              />
            </Inline>
          </Draft.CardSection>
          {isSettingsExpanded && (
            <Draft.CardSection>
              <Stack gap="m">
                <Draft.FormField fieldLabel="Delay (ms)">
                  <Draft.TextInput
                    max={5000}
                    min={0}
                    onChange={(e) => {
                      updateMockConfig({ delay: Number(e.target.value) });
                    }}
                    step={100}
                    type="number"
                    value={mockConfig.delay}
                  />
                </Draft.FormField>

                <Draft.FormField fieldLabel="Error Rate (0-1)">
                  <Draft.TextInput
                    max={1}
                    min={0}
                    onChange={(e) => {
                      updateMockConfig({ failureRate: Number(e.target.value) });
                    }}
                    step={0.1}
                    type="number"
                    value={mockConfig.failureRate}
                  />
                  <FieldMessage helpText={mockConfig.failureRate > 0 ? '' : 'Errors disabled at 0'} isVisible />
                </Draft.FormField>

                <Draft.FormField fieldLabel="Error Status Code">
                  <Draft.TextInput
                    disabled={mockConfig.failureRate === 0}
                    max={599}
                    min={400}
                    onChange={(e) => {
                      updateMockConfig({ statusCode: Number(e.target.value) });
                    }}
                    step={1}
                    type="number"
                    value={mockConfig.statusCode || 500}
                  />
                </Draft.FormField>

                <Draft.FormField fieldLabel="Error Message">
                  <Draft.TextInput
                    disabled={mockConfig.failureRate === 0}
                    onChange={(e) => {
                      updateMockConfig({ errorMessage: e.target.value });
                    }}
                    value={mockConfig.errorMessage || 'Mock server error'}
                  />
                </Draft.FormField>
              </Stack>
            </Draft.CardSection>
          )}
        </Draft.Card>

        {/* Mock Data */}
        <Stack gap="m">
          <Inline alignItems="center" justifyContent="space-between">
            <Text
              css={css`
                font-weight: 600;
                font-size: 16px;
              `}
            >
              API Mocks
            </Text>
            <Inline gap="xs">
              <Draft.Button label="Reset to Defaults" onClick={handleResetToDefaults} variant="outlined">
                Reset
              </Draft.Button>
              <IconButton label="Export Store" onClick={handleExportStore} title="Export all mocks and settings">
                <DownloadOutlined />
              </IconButton>
              <IconButton label="Import Store" onClick={handleImportStore} title="Import mocks and settings">
                <UploadOutlined />
              </IconButton>
              <IconButton label="Add Mock" onClick={showAddModal}>
                <PlusOutlined />
              </IconButton>
            </Inline>
          </Inline>

          <Inline gap="s" width="scale">
            <Draft.Button
              isFullWidth
              label="Cards"
              onClick={() => setViewMode('cards')}
              variant={viewMode === 'cards' ? 'contained' : 'ghost'}
            >
              Cards
            </Draft.Button>
            <Draft.Button
              isFullWidth
              label="3D View"
              onClick={() => setViewMode('3d')}
              variant={viewMode === '3d' ? 'contained' : 'ghost'}
            >
              3D View
            </Draft.Button>
          </Inline>

          {viewMode === '3d' ? (
            <Box
              css={css`
                width: 100%;
                height: 400px;
                margin-top: 16px;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                overflow: hidden;
              `}
            >
              <ThreeJSScene
                activeMocks={activeMocks}
                height="400px"
                mocks={mocks}
                onSelectMock={(id) => {
                  setSelectedMock(id);
                  showEditModal(mocks[id]);
                }}
              />
            </Box>
          ) : (
            <Stack gap="m">
              {Object.values(mocks)
                // Filter out token mocks by default unless explicitly searching for them
                .filter((mock) => !mock.id.includes('token') || mock.id === selectedMock)
                // Sort mocks by type: site first, then playlist, then videos
                .sort((a, b) => {
                  // Helper function to determine mock type
                  const getMockType = (mock: MockItem): number => {
                    const endpoint = mock.endpoint.toLowerCase();
                    if (endpoint.includes('sites') || endpoint.includes('feed')) return 1;
                    if (endpoint.includes('channels')) return 2;
                    if (endpoint.includes('playlist')) return 3;
                    if (endpoint.includes('video')) return 4;
                    return 5; // Other types
                  };

                  // Sort by type first
                  const typeA = getMockType(a);
                  const typeB = getMockType(b);
                  if (typeA !== typeB) return typeA - typeB;

                  // If same type, sort alphabetically by description
                  return (a.description || '').localeCompare(b.description || '');
                })
                .map((mock) => (
                  <EnhancedMockCard
                    key={mock.id}
                    isActive={activeMocks.includes(mock.id)}
                    isConnected={isConnected(mock.id)}
                    mock={mock}
                    onDelete={handleDeleteMock}
                    onEdit={showEditModal}
                    onToggle={handleToggleMock}
                    onUpdate={updateMock}
                  />
                ))}
            </Stack>
          )}

          {/* Data Fetcher Components */}
          <Stack gap="m">
            <YouTubeDataFetcherCard />
            <VimeoDataFetcherCard />
            <BoxCastDataFetcherCard />
            <SubSplashDataFetcherCard />
          </Stack>
          <Draft.Button
            label="Logout"
            onClick={() => {
              setMode('light');
              logout();
            }}
            variant="contained"
          >
            Logout
          </Draft.Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MockControlPanel;
